import { gql } from "@apollo/client";
import { Button, Dialog, useDialogViews } from "@resource/atlas";
import { CONTENT_TEMPLATE_FOR_EDITOR_FRAGMENT } from "components/GuideEditor/types/ContentTemplate";
import { EMAIL_TEMPLATE_FOR_EDITOR_FRAGMENT } from "components/GuideEditor/types/EmailTemplate";
import { Stage } from "components/GuideTemplateMapper/GuideTemplateMapper";
import { ModalName, useModals } from "components/ModalProvider";
import {
  GuideTemplatesForSelect_currentOrganization_guideTemplates as GuideTemplate,
  JobRoleGuideTemplateSetupStatus,
  OpenJobForTableFragment,
  UseGuideTemplate,
  UseGuideTemplateVariables,
} from "generated/schemaTypes";
import { useState } from "react";
import useMutation, { ToastDisplayCondition } from "utils/useMutation";

import GuideTemplateSelectionView from "./GuideTemplateSelectionView";
import MapStagesView from "./MapStagesView";
import ZeusGuideTemplateSelectionView from "./ZeusGuideTemplateSelectionView";

const USE_GUIDE_TEMPLATE = gql`
  mutation UseGuideTemplate($input: MapGuideTemplateInput!) {
    mapGuideTemplate(input: $input) {
      message
      code
      success
      jobRoleGuideTemplates {
        ...JobRoleGuideTemplateJobRoleGuideTemplate
      }
      atsStageWithJobRoleGuideTemplateIds {
        id: atsStageName
        name: atsStageName
        jobRoleGuideTemplateIds
        guideTemplateStageIds
      }
    }
  }
  fragment JobRoleGuideTemplateJobRoleGuideTemplate on JobRoleGuideTemplate {
    id
    jobRoleGuideTemplateStages {
      id
      name
    }
    guideTemplate {
      id
      name
      isGlobal
      department {
        id
        name
      }
    }
    ...JobRoleGuideTemplateStagePreviewJobRoleGuideTemplate
  }
  fragment JobRoleGuideTemplateStagePreviewJobRoleGuideTemplate on JobRoleGuideTemplate {
    id
    jobRoleGuideTemplateStages {
      id
      name
      description
      position
      contentTemplate {
        ...ContentTemplateForEditorFragment
      }
      emailTemplate {
        ...EmailTemplateForEditorFragment
      }
    }
  }
  ${EMAIL_TEMPLATE_FOR_EDITOR_FRAGMENT}
  ${CONTENT_TEMPLATE_FOR_EDITOR_FRAGMENT}
`;

const OPEN_JOB_FRAGMENT = gql`
  fragment OpenJobForTableFragment on OpenJob {
    id
    setupStatus
  }
`;

interface UseGuideTemplateModalProps {
  jobRoleGuideTemplateIds: string[];
  jobSetupStatus?: JobRoleGuideTemplateSetupStatus;
  onAssign?(): Promise<void> | void;
  guideTemplate?: GuideTemplate | null;
  singleJobName?: string;
  showSuccessToast?: boolean;
  /** Only use if mapping a single job role */
  currentGuideTemplateId?: string;
  isZeusJob: boolean;
}

function UseGuideTemplateModal({
  jobRoleGuideTemplateIds,
  jobSetupStatus,
  onAssign,
  guideTemplate,
  singleJobName,
  showSuccessToast = true,
  isZeusJob,
  currentGuideTemplateId,
}: UseGuideTemplateModalProps) {
  const modalManager = useModals();
  const [selectedGuideTemplate, setSelectedGuideTemplate] =
    useState<GuideTemplate | null>(guideTemplate ?? null);
  const [mapping, setMapping] = useState<Record<string, Stage[]>>({});
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [assignGuideTemplate, { loading: isAssigning }] = useMutation<
    UseGuideTemplate,
    UseGuideTemplateVariables
  >(USE_GUIDE_TEMPLATE, {
    displayToastsOn: showSuccessToast
      ? ToastDisplayCondition.ALL
      : ToastDisplayCondition.ERROR,
  });
  const onSave = async () => {
    const guideTemplateStages = Object.keys(mapping).map(
      (guideTemplateStageId) => {
        const mappedStages = mapping[guideTemplateStageId];
        const atsStageNames = mappedStages.map(
          (mappedStage) => mappedStage.name
        );
        return { id: guideTemplateStageId, atsStageNames };
      }
    );

    await assignGuideTemplate({
      variables: {
        input: {
          jobSetupStatus,
          guideTemplateId: selectedGuideTemplate?.id ?? "",
          jobRoleGuideTemplateIds,
          guideTemplateStages,
        },
      },
      update: (cache) => {
        if (!jobSetupStatus) {
          return;
        }

        jobRoleGuideTemplateIds.forEach((jobRoleGuideTemplateId) => {
          const cachedJRGT = cache.readFragment<OpenJobForTableFragment>({
            fragment: OPEN_JOB_FRAGMENT,
            fragmentName: "OpenJobForTableFragment",
            id: `OpenJob:${jobRoleGuideTemplateId}`,
          });
          if (!cachedJRGT) {
            return;
          }

          cache.writeFragment<OpenJobForTableFragment>({
            fragment: OPEN_JOB_FRAGMENT,
            fragmentName: "OpenJobForTableFragment",
            id: `OpenJob:${jobRoleGuideTemplateId}`,
            data: {
              ...cachedJRGT,
              setupStatus: jobSetupStatus,
            },
          });
        });
      },
    });
    await onAssign?.();
    modalManager.close();
  };

  const { dialogContentProps } = useDialogViews(
    {
      templateSelect: {
        size: "large",
        view: ({ setView, closeDialog, initialFocusRef }) => (
          <Dialog.View
            header={{
              title: "Select a template",
            }}
            footer={{
              leftActions: <Button onClick={closeDialog}>Cancel</Button>,
              rightActions: (
                <>
                  {isZeusJob ? (
                    <Button
                      ref={initialFocusRef}
                      isLoading={isAssigning}
                      onClick={() => {
                        if (isZeusJob) {
                          onSave();
                        } else {
                          setView("stageMapping");
                        }
                      }}
                    >
                      Use template
                    </Button>
                  ) : (
                    <Button
                      ref={initialFocusRef}
                      onClick={() => {
                        setView("stageMapping");
                      }}
                    >
                      Continue
                    </Button>
                  )}
                </>
              ),
            }}
            hasPadding={false}
          >
            {isZeusJob ? (
              <ZeusGuideTemplateSelectionView
                selectedGuideTemplate={selectedGuideTemplate}
                setSelectedGuideTemplate={setSelectedGuideTemplate}
                currentGuideTemplateId={currentGuideTemplateId}
              />
            ) : (
              <GuideTemplateSelectionView
                selectedGuideTemplate={selectedGuideTemplate}
                setSelectedGuideTemplate={setSelectedGuideTemplate}
              />
            )}
          </Dialog.View>
        ),
      },
      stageMapping: {
        backTarget: "templateSelect",
        view: ({ goBack }) => (
          <Dialog.View
            header={{
              separatorVisible: "always" as const,
              title: "Map Greenhouse stages to template stages",
            }}
            footer={{
              leftActions: <Button onClick={goBack}>Back</Button>,
              rightActions: (
                <Button
                  variant="primary"
                  onClick={onSave}
                  disabled={isLoadingPlan || isAssigning}
                >
                  {isAssigning ? "Saving" : "Save and assign"}
                </Button>
              ),
              separatorVisible: "always" as const,
            }}
            hasPadding={false}
          >
            {selectedGuideTemplate && (
              <MapStagesView
                jobRoleGuideTemplateIds={jobRoleGuideTemplateIds}
                singleJobName={singleJobName}
                planId={selectedGuideTemplate.id}
                mapping={mapping}
                setMapping={setMapping}
                setIsLoadingPlan={setIsLoadingPlan}
              />
            )}
          </Dialog.View>
        ),
      },
    },
    { initialView: "templateSelect" }
  );

  return (
    <Dialog.Root
      open={modalManager.isActive(ModalName.GUIDE_TEMPLATE_SELECTOR)}
      setOpen={(open) => {
        if (!open) {
          setSelectedGuideTemplate(null);
          setMapping({});
          modalManager.close();
        }
      }}
    >
      <Dialog.Content size="large" hasPadding={false} {...dialogContentProps} />
    </Dialog.Root>
  );
}

export default UseGuideTemplateModal;
